import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, signInWithCustomToken } from "../Firebase";
import axios from "axios";
import DeviceUtils from "../components/utils/deviceType";
import {
  hasBetaOptIn,
  setBetaOptIn,
  clearBetaOptIn,
  clearLegacyRoutingCookie,
} from "../components/utils/betaOptIn";
import { runMigrationCleanup } from "../components/utils/migrationCleanup";
import { handOverToNewSite } from "../components/utils/cacheBust";
import {
  getFirestore,
  doc,
  onSnapshot,
  getDoc,
  setDoc,
} from "firebase/firestore";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  let initialUser = null;
  let initialUserDocument = null;
  const storedUser = localStorage.getItem('currentUser');
  const storedUserDocument = localStorage.getItem('userDocument');

  if (storedUser) {
    const parsedUser = JSON.parse(storedUser);
    // An anonymous session is not an account, and this site has no use for one.
    // Firebase persistence is per origin, so a visitor who picked one up
    // elsewhere on departureboard.io comes back here already "signed in" as a
    // user with no email. Rendering off the cached copy would show them a
    // signed-in page for the moment before onAuthStateChanged fires and, worse,
    // point the Firestore listener below at the anonymous uid — which creates
    // that user a Users document. The listener that does the actual signing out
    // is in the auth effect; this only keeps the cached copy from acting first.
    if (!parsedUser.isAnonymous) {
      initialUser = parsedUser;
      if (storedUserDocument) {
        initialUserDocument = JSON.parse(storedUserDocument);
      }
    }
  } else if (storedUserDocument) {
    initialUserDocument = JSON.parse(storedUserDocument);
  }

  const [currentUser, setCurrentUser] = useState(initialUser);
  const [userDocument, setUserDocument] = useState(initialUserDocument);
  const [loading, setLoading] = useState(true);
  // Whether the document in state came from Firestore this page load, as
  // opposed to out of localStorage. Only one thing needs to know — withdrawing
  // beta access, below, which must never act on a cached copy of a field that
  // has since changed.
  const [documentLive, setDocumentLive] = useState(false);

  const getProviderName = (user) => {
    if (user && user.providerData && user.providerData.length > 0) {
      const providerId = user.providerData[0].providerId;
      const providerName = providerId.split(".")[0];
      return providerName;
    }
    return null;
  };

  useEffect(() => {
    const createUserDocument = async (user) => {
      const firestore = getFirestore();
      const userRef = doc(firestore, "Users", user.uid);

      const userData = {
        uid: user.uid,
        email: user.email,
        isPro: false,
        idp: getProviderName(user),
      };

      await setDoc(userRef, userData);
    };

    if (!currentUser) {
      const queryParams = new URLSearchParams(window.location.search);
      const key = queryParams.get("key");
      const userID = queryParams.get("userID");
      const token = queryParams.get("token");

      if (key && userID && token && DeviceUtils.isIOSStandalonePWA()) {
        axios
          .post(
            `${process.env.REACT_APP_DBAPI_URL}/api/v1.0/mintLoginTransfer`,
            {
              key,
              userID,
              token,
            }
          )
          .then((response) => {
            const { token: firebaseToken } = response.data;
            console.log(token);
            return signInWithCustomToken(auth, firebaseToken)
              .then((userCredential) => {
                console.log("User successfully signed in: ", userCredential.user);
                setCurrentUser(userCredential.user);
                localStorage.setItem('currentUser', JSON.stringify(userCredential.user));
              })
              .catch((error) => {
                console.error("Error during Firebase sign-in:", error);
              });
          })
          .catch((error) => {
            console.error("Error during login transfer:", error);
          });
      }
    } else {
      const firestore = getFirestore();
      const userRef = doc(firestore, "Users", currentUser.uid);

      const unsubscribe = onSnapshot(
        userRef,
        async (docSnapshot) => {
          // A snapshot carrying this browser's own un-acknowledged write is not
          // news from Firestore, it is Firestore showing us what we just typed.
          // Skipping it is load bearing rather than tidy.
          //
          // Pressing "Try the new site" writes `betaOptIn` and *awaits the
          // server*, but the SDK applies the write locally first and raises a
          // snapshot for it immediately. Acting on that ran the reconciliation
          // below — cookie, cleanup, hand over — and navigated the document away
          // while the write was still in flight, so the field never reached
          // Firestore. The browser ended up on the new site with nothing on the
          // account to say so: no other device followed, and the next sign-in
          // here read an absent `betaOptIn` and cleared the cookie again.
          //
          // It also hid the failures. A write refused by the rules is rolled
          // back and re-announced on this listener, which is how Profile.js
          // gets to show its error — but only if the page is still here to
          // show it.
          //
          // Nothing is lost by waiting: Profile.js drives its own case in
          // order, once the server has actually accepted the write.
          if (docSnapshot.metadata.hasPendingWrites) {
            return;
          }
          if (docSnapshot.exists()) {
            setUserDocument(docSnapshot.data());
            setDocumentLive(true);
            localStorage.setItem('userDocument', JSON.stringify(docSnapshot.data()));
            setLoading(false);
          } else {
            await createUserDocument(currentUser);
            const newUserSnapshot = await getDoc(userRef);
            if (newUserSnapshot.exists()) {
              setUserDocument(newUserSnapshot.data());
              setDocumentLive(true);
              localStorage.setItem('userDocument', JSON.stringify(newUserSnapshot.data()));
              setLoading(false);
            } else {
              console.error("User document not found");
            }
          }
        },
        (error) => {
          console.error("Error fetching user document:", error);
        }
      );

      const storedUserDocument = localStorage.getItem('userDocument');
      if (storedUserDocument) {
        setUserDocument(JSON.parse(storedUserDocument));
        setLoading(false);
      }

      return () => {
        unsubscribe();
      };
    }
  }, [currentUser]);

  useEffect(() => {
    const authUnsubscribe = auth.onAuthStateChanged((user) => {
      // Anonymous sessions are signed out rather than adopted. Everything this
      // site does with a user — the Users document, the beta reconciliation
      // below, the profile — assumes an account behind it, and an anonymous
      // user has none: no email, no identity provider, and a uid that belongs
      // to one browser and disappears with it.
      //
      // The sign out lands back on this same callback with null, which takes
      // the signed-out path immediately below; state is cleared here as well as
      // there so nothing renders against the anonymous user in the meantime.
      const signedInUser = user && user.isAnonymous ? null : user;

      if (user && user.isAnonymous) {
        auth.signOut().catch((error) => {
          console.error("Error signing out anonymous user:", error);
        });
      }

      setCurrentUser(signedInUser);
      if (!signedInUser) {
        setUserDocument(null);
        setDocumentLive(false);
        setLoading(false);
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userDocument');
      }

      // The beta cookie is deliberately not touched, in either direction. It is
      // a decision about this browser rather than a property of the session:
      // signing out is not leaving the beta, and the new site is perfectly
      // happy with a signed out visitor. It is also what stops the signed-out
      // callback every cold load fires, before Firebase has restored the
      // session, from quietly evicting somebody.
      //
      // What does go is the cookie the edge used to route on, which nothing
      // reads any more.
      clearLegacyRoutingCookie();
    });

    return () => {
      authUnsubscribe();
    };
  }, []);

  // Reconciling the account with the browser, which is the whole of how the beta
  // follows somebody around.
  //
  // `betaOptIn` on the user's Firestore document is the decision: they made it
  // once, on whatever device they happened to be using. The cookie is only that
  // decision written somewhere the edge can read it, and it is per browser — so
  // this is what puts it on the second browser, and the third, every time they
  // sign in here. Signing in on a new phone is the case this exists for.
  //
  // Being here at all with the cookie set means the routing did not happen: the
  // browser answered from its own cache without ever asking the edge. Handing
  // over deals with that; see cacheBust.js.
  //
  // Only ever against a document Firestore has actually just sent. The cached
  // copy in localStorage is there to render the page before the snapshot
  // arrives, and moving somebody between sites on the strength of a field read
  // out of it would be acting on what was true last time they were here.
  useEffect(() => {
    const cookieSet = hasBetaOptIn();

    if (documentLive && userDocument) {
      // Eligibility is rechecked, not just the opt in. An operator withdrawing
      // access cannot reach a cookie in somebody's browser, so this is the only
      // moment the withdrawal can take effect — and it is a fair one: they are
      // looking at the old site with their account signed in.
      const belongs =
        Boolean(userDocument.betaEligible) && Boolean(userDocument.betaOptIn);

      if (!belongs) {
        if (cookieSet) {
          clearBetaOptIn();
        }
        return;
      }

      if (!cookieSet) {
        setBetaOptIn();
      }
    } else if (!cookieSet) {
      // Signed out, or the document has not arrived. The cookie is the only
      // thing to go on, and it says nothing.
      return;
    }

    // The cleanup has to finish first: handOverToNewSite navigates away, and
    // that would cut its cache and service worker deletion off mid flight.
    runMigrationCleanup().then(handOverToNewSite);
  }, [userDocument, documentLive]);

  const contextValue = {
    currentUser,
    userDocument,
    loading,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
